import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowLeft, Plus, Search, ChevronDown } from 'lucide-react';
import TipTapEditor from './TipTapEditor';
import DocumentsList from './DocumentsList';
import { documentService } from '../../services/documentService';
import { useAuth } from '../../contexts/AuthContext';
import focusService from '../../services/focusService';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import HardBreak from '@tiptap/extension-hard-break';

/**
 * DocumentsSidebar Component
 * Right sidebar for document editing with TipTap
 * Redesigned to show list first, with filtering and search
 */
export default function DocumentsSidebar({ onClose, initialDocumentId = null }) {
  const { user } = useAuth();
  const [currentDocument, setCurrentDocument] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [focuses, setFocuses] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [view, setView] = useState('list'); // 'list' or 'editor'
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved');
  const titleInputRef = useRef(null);

  // Initialize TipTap editor with Gmail-style line breaks
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: false,
      }),
      HardBreak.extend({
        addKeyboardShortcuts() {
          return {
            Enter: () => this.editor.commands.setHardBreak(),
            'Shift-Enter': () => this.editor.commands.createParagraphNear(),
          }
        },
      }),
    ],
    content: currentDocument?.content_json || {
      type: 'doc',
      content: [{ type: 'paragraph' }]
    },
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[500px] px-8 py-6'
      }
    },
    onUpdate: ({ editor }) => {
      // Trigger autosave
      if (currentDocument) {
        setSaveStatus('saving');
        handleAutosave(editor.getJSON());
      }
    }
  });

  // Handle autosave with validation
  const handleAutosave = async (contentJson) => {
    if (!currentDocument || !user) return;
    
    // Check if document has content (not empty)
    const hasContent = contentJson?.content?.some(node => {
      if (node.type === 'paragraph' && node.content) {
        return node.content.some(c => c.text && c.text.trim().length > 0);
      }
      return node.type !== 'paragraph' || (node.content && node.content.length > 0);
    });

    const hasTitle = titleValue && titleValue.trim() !== '' && titleValue !== 'Untitled';

    // Only save if there's a title OR content
    if (!hasTitle && !hasContent) {
      setSaveStatus('saved');
      return;
    }

    try {
      await documentService.updateDocument(currentDocument.id, { 
        content_json: contentJson,
        title: titleValue 
      });
      setSaveStatus('saved');
    } catch (err) {
      console.error('Failed to autosave:', err);
      setSaveStatus('saved');
    }
  };

  // Load user's focuses for filtering
  useEffect(() => {
    const loadFocuses = async () => {
      if (!user) return;
      try {
        const userFocuses = await focusService.getUserFocuses(user.id);
        setFocuses(userFocuses || []);
      } catch (err) {
        console.error('Failed to load focuses:', err);
      }
    };
    loadFocuses();
  }, [user]);

  // Initial load - always start with list view unless initialDocumentId is provided
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      setError(null);
      
      await loadDocuments();
      
      if (initialDocumentId) {
        // Load specific document and go to editor
        const doc = documents.find(d => d.id === initialDocumentId);
        if (doc) {
          handleSelectDocument(doc);
        } else {
          // Try loading from database
          const { data, error: loadError } = await documentService.getDocument(initialDocumentId);
          if (data && !loadError) {
            handleSelectDocument(data);
          }
        }
      } else {
        // Start with list view
        setView('list');
      }
      
      setIsLoading(false);
    };

    initialize();
  }, [initialDocumentId, user]);

  // Load documents list
  const loadDocuments = async () => {
    if (!user) {
      // Show placeholder documents in demo mode
      setDocuments([
        {
          id: 'demo-1',
          title: 'Welcome Guide',
          type: 'doc',
          focus_ids: [],
          summary: 'Get started with the docs editor',
          updated_at: new Date().toISOString(),
          content_json: {
            type: 'doc',
            content: [
              { type: 'heading', attrs: { level: 1 }, content: [{ type: 'text', text: 'Welcome to Documents!' }] },
              { type: 'paragraph', content: [{ type: 'text', text: 'This is a demo document.' }] }
            ]
          }
        },
        {
          id: 'demo-2',
          title: 'My Project Plan',
          type: 'plan',
          focus_ids: [],
          summary: 'Complete feature X and launch Q1',
          updated_at: new Date(Date.now() - 86400000).toISOString(),
          content_json: {
            type: 'doc',
            content: [
              { type: 'heading', attrs: { level: 1 }, content: [{ type: 'text', text: 'Project Plan' }] }
            ]
          }
        }
      ]);
      return;
    }

    const { data, error: fetchError } = await documentService.getUserDocuments();
    if (data && !fetchError) {
      // Add summaries to documents
      const docsWithSummaries = data.map(doc => ({
        ...doc,
        summary: extractSummary(doc.content_json)
      }));
      setDocuments(docsWithSummaries);
      setError(null);
    } else {
      setError(fetchError?.message || 'Failed to load documents');
      setDocuments([]);
    }
  };

  // Extract summary from document content
  const extractSummary = (contentJson) => {
    if (!contentJson?.content) return 'No content';
    
    for (const node of contentJson.content) {
      if (node.type === 'paragraph' && node.content) {
        const text = node.content
          .filter(c => c.text)
          .map(c => c.text)
          .join(' ')
          .trim();
        if (text) {
          return text.slice(0, 100) + (text.length > 100 ? '...' : '');
        }
      }
    }
    return 'Empty document';
  };

  // Handle creating new document
  const handleNewDocument = async () => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    const { data, error: createError } = await documentService.createDocument({
      user_id: user.id,
      title: '',
      type: 'doc',
      content_json: { type: 'doc', content: [{ type: 'paragraph' }] }
    });

    if (data && !createError) {
      handleSelectDocument(data);
      await loadDocuments();
    } else {
      setError(createError?.message || 'Failed to create document');
    }
  };

  // Handle selecting a document from list
  const handleSelectDocument = (doc) => {
    setCurrentDocument(doc);
    setTitleValue(doc.title || '');
    setView('editor');
    if (editor && doc.content_json) {
      editor.commands.setContent(doc.content_json);
    }
  };

  // Handle going back to list
  const handleBackToList = () => {
    setView('list');
    setCurrentDocument(null);
    loadDocuments();
  };

  // Handle title update
  const handleTitleBlur = async () => {
    setIsEditingTitle(false);
    if (currentDocument && titleValue !== currentDocument.title && user) {
      try {
        await documentService.updateDocument(currentDocument.id, { title: titleValue });
        setCurrentDocument({ ...currentDocument, title: titleValue });
      } catch (err) {
        console.error('Failed to update title:', err);
      }
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleBlur();
    }
  };

  // Handle focus change
  const handleFocusChange = async (newFocusIds) => {
    if (!currentDocument || !user) return;
    
    try {
      setSaveStatus('saving');
      await documentService.updateDocument(currentDocument.id, { 
        focus_ids: newFocusIds 
      });
      setCurrentDocument({ ...currentDocument, focus_ids: newFocusIds });
      setSaveStatus('saved');
      // Reload documents list to show updated focus tags
      await loadDocuments();
    } catch (err) {
      console.error('Failed to update focus:', err);
      setSaveStatus('saved');
    }
  };

  // Filter documents based on selected focus
  const filteredDocuments = documents.filter(doc => {
    // Filter by focus
    if (selectedFilter !== 'All') {
      const focusMatch = doc.focus_ids && doc.focus_ids.includes(selectedFilter);
      if (!focusMatch) return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        doc.title?.toLowerCase().includes(query) ||
        doc.summary?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isEditingTitle]);

  if (isLoading) {
    return (
      <div className="w-[420px] border-l border-neutral-200 bg-white flex items-center justify-center h-full shadow-2xl">
        <div className="text-neutral-500 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="w-[420px] h-full bg-white border-l border-neutral-200/60 flex flex-col shadow-[-10px_0_40px_-15px_rgba(0,0,0,0.15)]">
      {/* Demo Mode Banner */}
      {error && !user && (
        <div className="bg-yellow-50 border-b border-yellow-200 p-2 flex-shrink-0">
          <p className="text-xs text-yellow-800 text-center">
            <span className="font-semibold">Demo Mode:</span> Sign in to save documents
          </p>
        </div>
      )}

      {/* LIST VIEW */}
      {view === 'list' && (
        <>
          {/* Header with Close Button */}
          <div className="border-b border-neutral-100 px-6 py-4 flex-shrink-0 bg-gradient-to-b from-white to-neutral-50/30">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-900">Documents</h2>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-all duration-200"
                aria-label="Close sidebar"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Action Buttons: New Doc + Search */}
          <div className="px-4 py-3 flex-shrink-0 space-y-3">
            <button
              onClick={handleNewDocument}
              className="flex items-center gap-2 text-[#7A47F5] hover:text-[#6B3FE0] transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              <span>New doc</span>
            </button>
            
            {/* Expandable Search */}
            <div className="relative">
              {!isSearchExpanded ? (
                <button
                  onClick={() => setIsSearchExpanded(true)}
                  className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors text-sm"
                >
                  <Search size={16} />
                  <span>Search</span>
                </button>
              ) : (
                <div className="relative animate-in fade-in slide-in-from-left-2 duration-200">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => {
                      if (!searchQuery) {
                        setIsSearchExpanded(false);
                      }
                    }}
                    placeholder="Search docs"
                    autoFocus
                    className="w-full pl-9 pr-3 py-1.5 bg-neutral-50 border border-neutral-300 rounded-lg text-sm text-neutral-700 placeholder:text-neutral-400 focus:outline-none focus:bg-white focus:border-[#7A47F5] focus:ring-2 focus:ring-purple-500/20 transition-all"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-neutral-100 mx-4 my-1"></div>

          {/* Filter Dropdown */}
          <div className="px-4 py-1 flex-shrink-0">
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors px-3 py-1.5 hover:bg-neutral-50 rounded-lg"
              >
                <span className="text-xs font-medium">View</span>
                <span className="px-2 py-0.5 bg-neutral-100 rounded-md text-xs font-medium text-neutral-700">
                  {selectedFilter === 'All' ? 'All' : focuses.find(f => f.id === selectedFilter)?.title || selectedFilter}
                </span>
                <ChevronDown size={13} className="text-neutral-400" />
              </button>

              {showFilterDropdown && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-neutral-200 rounded-xl shadow-xl z-50 py-2">
                  <div className="px-3 py-2 text-xs font-semibold text-neutral-400 uppercase">Filter By Focus</div>
                  <button
                    onClick={() => {
                      setSelectedFilter('All');
                      setShowFilterDropdown(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 transition-colors ${
                      selectedFilter === 'All' ? 'text-purple-600 font-medium bg-purple-50' : 'text-neutral-700'
                    }`}
                  >
                    All
                  </button>
                  {focuses && focuses.length > 0 && focuses
                    .sort((a, b) => a.title.localeCompare(b.title))
                    .map(focus => (
                      <button
                        key={focus.id}
                        onClick={() => {
                          setSelectedFilter(focus.id);
                          setShowFilterDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 transition-colors ${
                          selectedFilter === focus.id ? 'text-purple-600 font-medium bg-purple-50' : 'text-neutral-700'
                        }`}
                      >
                        {focus.title}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Documents List */}
          <DocumentsList
            documents={filteredDocuments}
            onSelectDocument={handleSelectDocument}
            focuses={focuses}
          />
        </>
      )}

      {/* EDITOR VIEW */}
      {view === 'editor' && currentDocument && (
        <>
          {/* Header with Back Arrow and Editable Title */}
          <div className="border-b border-neutral-100 px-6 py-4 flex-shrink-0 bg-gradient-to-b from-white to-neutral-50/30">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToList}
                className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-all duration-200"
                aria-label="Back to list"
              >
                <ArrowLeft size={18} />
              </button>
              
              <div className="flex-1 min-w-0">
                {isEditingTitle ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onBlur={handleTitleBlur}
                    onKeyDown={handleTitleKeyDown}
                    placeholder="Untitled"
                    className="w-full text-xl font-semibold text-neutral-900 border-b-2 border-[#7A47F5] focus:outline-none bg-transparent pb-1"
                  />
                ) : (
                  <h2
                    onClick={() => setIsEditingTitle(true)}
                    className="text-xl font-semibold text-neutral-900 truncate cursor-text hover:text-[#7A47F5] transition-all duration-200"
                    title="Click to edit"
                  >
                    {titleValue || 'Untitled'}
                  </h2>
                )}
              </div>

              <div className="flex items-center gap-3">
                <div className={`text-xs font-medium transition-colors duration-200 ${
                  saveStatus === 'saving' ? 'text-[#7A47F5]' : 'text-green-600'
                }`}>
                  {saveStatus === 'saving' ? 'Saving...' : 'Saved'}
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-all duration-200"
                  aria-label="Close sidebar"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Editor Content */}
          <div className="flex-1 overflow-y-auto">
            <TipTapEditor
              editor={editor}
              content={currentDocument.content_json}
              currentDocument={currentDocument}
              focuses={focuses}
              onFocusChange={handleFocusChange}
              onUpdate={() => {
                // Handled by editor's onUpdate callback
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

